import { useState, useEffect, useRef } from 'react';
import { Search, Printer, Download, Plus, Minus, Settings, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import QRCode from 'qrcode';
import { useToast } from '@/hooks/use-toast';
import { Layout } from '@/components/layout/Layout';
import * as XLSX from 'xlsx';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { productService, type ProductRow } from '@/lib/packages';

// Helper function to get brand from product meta
const getBrandFromMeta = (meta: any): string => {
  if (!meta) return '';
  if (typeof meta === 'object' && meta.brand) return meta.brand;
  return '';
};

interface LabelSize {
  id: string;
  name: string;
  width: number;
  height: number;
  unit: string;
}

const labelSizes: LabelSize[] = [
  { id: 'small', name: '39.7×20mm', width: 39.7, height: 20, unit: 'mm' },
  { id: 'medium', name: '60×40mm', width: 60, height: 40, unit: 'mm' },
  { id: 'large', name: '70×50mm', width: 70, height: 50, unit: 'mm' },
];

interface SelectedProduct {
  id: string;
  name: string;
  code: string;
  brand: string;
  quantity: number;
  price?: number;
}

export default function QRPrint() {
  const { toast } = useToast();
  const { user, companyId } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [labelSize, setLabelSize] = useState<LabelSize>(labelSizes[1]);
  const [includeLogo, setIncludeLogo] = useState(true);
  const [includePrice, setIncludePrice] = useState(false);
  const [columns, setColumns] = useState(2);
  const [qrDataUrls, setQrDataUrls] = useState<{ [key: string]: string }>({});
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Fetch products from database (uses pagination to handle >1000 rows)
  useEffect(() => {
    const fetchProducts = async () => {
      if (!companyId) return;
      
      setLoading(true);
      try {
        // Use paginated getAllProducts to fetch all active products
        const allProducts = await productService.getAllProducts(companyId, {
          includeInactive: false
        });

        setProducts(allProducts);
      } catch (error) {
        console.error('Error in fetchProducts:', error);
        toast({
          title: "Hata", 
          description: "Ürünler yüklenemedi",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    void fetchProducts();
  }, [companyId, toast]);

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.item_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getBrandFromMeta(product.meta).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Generate QR codes for selected products
  useEffect(() => {
    const generateQRCodes = async () => {
      const { generateBrandedQRCode } = await import('@/lib/qr-utils');
      
      const qrPromises = selectedProducts.map(async (product) => {
        try {
          const qrProductData = {
            id: product.id,
            name: product.name,
            item_code: product.code,
            brand: product.brand,
            company_id: companyId!
          };
          
          const qrDataUrl = await generateBrandedQRCode(qrProductData, { size: 200 });
          return { id: product.id, dataUrl: qrDataUrl };
        } catch (error) {
          console.error('QR generation error:', error);
          return { id: product.id, dataUrl: '' };
        }
      });

      const results = await Promise.all(qrPromises);
      const qrMap = results.reduce((acc, { id, dataUrl }) => {
        acc[id] = dataUrl;
        return acc;
      }, {} as { [key: string]: string });

      setQrDataUrls(qrMap);
    };

    if (selectedProducts.length > 0 && companyId) {
      void generateQRCodes();
    }
  }, [selectedProducts, companyId]);

  const handleProductSelect = (product: ProductRow) => {
    const existing = selectedProducts.find(p => p.id === product.id);
    if (existing) {
      setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
    } else {
      setSelectedProducts(prev => [...prev, {
        id: product.id,
        name: product.name,
        code: product.item_code,
        brand: getBrandFromMeta(product.meta),
        quantity: 1,
        price: product.price
      }]);
    }
  };

  const updateQuantity = (productId: string, delta: number) => {
    setSelectedProducts(prev => prev.map(p => 
      p.id === productId 
        ? { ...p, quantity: Math.max(1, p.quantity + delta) }
        : p
    ));
  };

  const trimProductName = (name: string, maxLength: number = 25) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = generatePrintHTML();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    
    toast({
      title: "Yazdırma Hazır",
      description: "Etiketler yazıcıya gönderildi",
    });
  };

  const handleExportPDF = () => {
    // In a real implementation, you'd use a library like jsPDF
    toast({
      title: "Dışa Aktarma Başladı",
      description: "PDF oluşturma devam ediyor...",
    });
  };

  const handleExportExcel = async () => {
    if (selectedProducts.length === 0) {
      toast({
        title: "Ürün Seçilmedi",
        description: "Excel dosyası oluşturmak için en az bir ürün seçin",
        variant: "destructive",
      });
      return;
    }

    // Create worksheet data using universal QR links
    const { generateUniversalProductLink } = await import('@/lib/qr-utils');
    
    const excelData = selectedProducts.flatMap(product => 
      Array(product.quantity).fill(0).map((_, index) => ({
        'Ürün Adı': product.name,
        'Ürün Kodu': product.code,
        'Marka': product.brand,
        'Etiket No': `${index + 1}/${product.quantity}`,
        'QR Link': generateUniversalProductLink(product.id, companyId!),
        'Oluşturulma Tarihi': new Date().toLocaleDateString('tr-TR'),
        'Etiket Boyutu': labelSize.name,
        'Sütun Sayısı': columns,
        'Site': 'www.musterix.com'
      }))
    );

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const columnWidths = [
      { wch: 30 }, // Ürün Adı
      { wch: 15 }, // Ürün Kodu
      { wch: 15 }, // Marka
      { wch: 10 }, // Etiket No
      { wch: 50 }, // QR Link
      { wch: 15 }, // Oluşturulma Tarihi
      { wch: 15 }, // Etiket Boyutu
      { wch: 12 }, // Sütun Sayısı
      { wch: 20 }  // Site
    ];
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'QR Etiketleri');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
    const filename = `qr-etiketleri-${timestamp}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, filename);

    toast({
      title: "Excel Dosyası İndirildi",
      description: `${selectedProducts.reduce((acc, p) => acc + p.quantity, 0)} etiket bilgisi Excel dosyasına aktarıldı`,
    });
  };

  const handleExportExcelForLabelMachine = async () => {
    if (!companyId) {
      toast({
        title: "Hata",
        description: "Şirket bilgisi bulunamadı",
        variant: "destructive",
      });
      return;
    }

    if (products.length === 0) {
      toast({
        title: "Ürün Bulunamadı",
        description: "Excel dosyası oluşturmak için ürün bulunamadı",
        variant: "destructive",
      });
      return;
    }

    // Show loading toast
    toast({
      title: "Excel Oluşturuluyor",
      description: `${products.length} ürün için Excel dosyası hazırlanıyor...`,
    });

    try {
      // Create worksheet data with only product code, product name, and QR link
      const { generateUniversalProductLink } = await import('@/lib/qr-utils');
      
      const excelData = products.map(product => ({
        'Ürün Kodu': product.item_code,
        'Ürün Adı': product.name,
        'QR Kod Linki': generateUniversalProductLink(product.id, companyId)
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 20 }, // Ürün Kodu
        { wch: 40 }, // Ürün Adı
        { wch: 60 }  // QR Kod Linki
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Etiket Makinesi');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      const filename = `etiket-makinesi-${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);

      toast({
        title: "Excel Dosyası İndirildi",
        description: `${products.length} ürün bilgisi etiket makinesi için Excel dosyasına aktarıldı`,
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast({
        title: "Hata",
        description: "Excel dosyası oluşturulurken bir hata oluştu",
        variant: "destructive",
      });
    }
  };

  const generatePrintHTML = () => {
    const labelWidth = labelSize.width;
    const labelHeight = labelSize.height;
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Labels</title>
          <style>
            @page { margin: 0; }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 10mm;
              background: white;
            }
            .label-grid {
              display: grid;
              grid-template-columns: repeat(${columns}, 1fr);
              gap: 2mm;
            }
            .label {
              width: ${labelWidth}mm;
              height: ${labelHeight}mm;
              border: 1px solid #ccc;
              padding: 1mm;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              text-align: center;
              page-break-inside: avoid;
              font-size: 8px;
            }
            .qr-code {
              width: ${Math.min(labelWidth * 0.6, labelHeight * 0.6)}mm;
              height: ${Math.min(labelWidth * 0.6, labelHeight * 0.6)}mm;
            }
            .product-name {
              font-weight: bold;
              margin: 1mm 0;
              font-size: 7px;
            }
            .product-code {
              font-size: 6px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="label-grid">
            ${selectedProducts.flatMap(product => 
              Array(product.quantity).fill(0).map(() => `
                <div class="label">
                  ${qrDataUrls[product.id] ? `<img src="${qrDataUrls[product.id]}" class="qr-code" alt="QR Code" />` : ''}
                  <div class="product-name">${trimProductName(product.name, 20)}</div>
                  <div class="product-code">${product.code}</div>
                </div>
              `).join('')
            ).join('')}
          </div>
        </body>
      </html>
    `;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">QR Etiket Üretici</h1>
          <p className="text-muted-foreground">Ürünler için QR kod etiketleri oluşturun ve yazdırın</p>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Product Selection */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Ürün Seçimi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Input
                  placeholder="Ürünleri isim, kod veya markaya göre arayın..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />

                <div className="max-h-96 overflow-y-auto space-y-2">
                  {loading ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">Ürünler yükleniyor...</p>
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">
                        {searchTerm ? 'Arama kriterinize uygun ürün bulunamadı' : 'Henüz ürün bulunmuyor'}
                      </p>
                    </div>
                  ) : (
                    filteredProducts.map((product) => {
                      const isSelected = selectedProducts.some(p => p.id === product.id);
                      const brand = getBrandFromMeta(product.meta);
                      return (
                        <div
                          key={product.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            isSelected 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => handleProductSelect(product)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Checkbox checked={isSelected} />
                              <div>
                                <h4 className="font-medium">{product.name}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {product.item_code} {brand && `• ${brand}`}
                                </p>
                                {includePrice && (
                                  <p className="text-sm font-medium text-green-600">
                                    ₺{product.price.toFixed(2)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge variant="secondary">{product.category}</Badge>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selected Products */}
          {selectedProducts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Seçilen Ürünler ({selectedProducts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {selectedProducts.map((product) => (
                    <div key={product.id} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{trimProductName(product.name)}</p>
                        <p className="text-xs text-muted-foreground">{product.code}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(product.id, -1)}
                          disabled={product.quantity <= 1}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-8 text-center text-sm">{product.quantity}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updateQuantity(product.id, 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel - Label Preview & Settings */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Etiket Ayarları
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Etiket Boyutu</Label>
                <Select value={labelSize.id} onValueChange={(value) => {
                  const size = labelSizes.find(s => s.id === value) || labelSizes[1];
                  setLabelSize(size);
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {labelSizes.map((size) => (
                      <SelectItem key={size.id} value={size.id}>
                        {size.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Sütunlar</Label>
                <Select value={columns.toString()} onValueChange={(value) => setColumns(parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Sütun</SelectItem>
                    <SelectItem value="2">2 Sütun</SelectItem>
                    <SelectItem value="3">3 Sütun</SelectItem>
                    <SelectItem value="4">4 Sütun</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Logo Dahil Et</Label>
                  <Switch checked={includeLogo} onCheckedChange={setIncludeLogo} />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Fiyat Dahil Et</Label>
                  <Switch checked={includePrice} onCheckedChange={setIncludePrice} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          {selectedProducts.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Etiket Önizleme</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="grid gap-2 p-4 border-2 border-dashed border-border rounded-lg bg-muted/10"
                  style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
                >
                  {selectedProducts.slice(0, 6).flatMap(product => 
                    Array(Math.min(product.quantity, 2)).fill(0).map((_, index) => (
                      <div 
                        key={`${product.id}-${index}`}
                        className="border border-border bg-background p-2 rounded text-center"
                        style={{ 
                          aspectRatio: `${labelSize.width}/${labelSize.height}`,
                          fontSize: '8px'
                        }}
                      >
                        {qrDataUrls[product.id] && (
                          <img 
                            src={qrDataUrls[product.id]} 
                            alt="QR" 
                            className="w-12 h-12 mx-auto mb-1"
                          />
                        )}
                        <div className="font-semibold text-[6px] leading-tight">
                          {trimProductName(product.name, 15)}
                        </div>
                        <div className="text-[5px] text-muted-foreground">
                          {product.code}
                        </div>
                      </div>
                    ))
                  )}
                  {selectedProducts.reduce((acc, p) => acc + p.quantity, 0) > 6 && (
                    <div className="col-span-full text-center text-sm text-muted-foreground p-2">
                      +{selectedProducts.reduce((acc, p) => acc + p.quantity, 0) - 6} etiket daha...
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 mt-4">
                  <Button onClick={handlePrint} className="w-full">
                    <Printer className="w-4 h-4 mr-2" />
                    Etiketleri Yazdır
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportPDF} className="flex-1">
                      <Download className="w-4 h-4 mr-2" />
                      PDF İndir
                    </Button>
                    <Button variant="outline" onClick={handleExportExcel} className="flex-1">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Excel İndir
                    </Button>
                  </div>
                  <Button variant="outline" onClick={handleExportExcelForLabelMachine} className="w-full">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Etiket Makinesi İçin Excel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                  <Printer className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-2">Ürün Seçilmedi</h3>
                <p className="text-muted-foreground text-sm">
                  QR etiketleri oluşturmak için sol panelden ürün seçin
                </p>
              </CardContent>
            </Card>
          )}

          {/* Etiket Makinesi İçin Excel - Always Visible */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Etiket Makinesi İçin Excel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Tüm ürünler için Excel dosyası oluşturun. Dosya ürün kodu, ürün adı ve QR kod linki içerir.
                {products.length > 0 && (
                  <span className="block mt-1 font-medium text-foreground">
                    Toplam {products.length} ürün mevcut.
                  </span>
                )}
              </p>
              <Button 
                variant="outline" 
                onClick={handleExportExcelForLabelMachine} 
                className="w-full"
                disabled={loading || products.length === 0}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {loading ? 'Yükleniyor...' : 'Tüm Ürünleri Excel Olarak İndir'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </Layout>
  );
}